/**
 * Rewards Card
 * Displays what users earn from referrals
 */

import { Gift, Star, Zap, Trophy } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { REFERRAL_REWARDS } from '../utils/constants';

interface RewardItemProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}

function RewardItem({ icon: Icon, title, description }: RewardItemProps) {
  return (
    <div className="bg-white/10 rounded-xl p-4 hover:bg-white/15 transition-colors">
      <div className="flex items-center gap-3 mb-2">
        <Icon className="w-5 h-5 text-yellow-300" />
        <span className="font-semibold text-white">{title}</span>
      </div>
      <p className="text-sm text-white/70">{description}</p>
    </div>
  );
}

export function RewardsCard() {
  const { t } = useLanguage();

  const rewards = [
    {
      icon: Star,
      title: t('referrals.proDays', `${REFERRAL_REWARDS.PRO_DAYS} Pro Days`),
      description: t(
        'referrals.proDaysDescription',
        `Get ${REFERRAL_REWARDS.PRO_DAYS} days of Pro features for each qualified referral`
      ),
    },
    {
      icon: Zap,
      title: `+${REFERRAL_REWARDS.XP} XP`,
      description: t('referrals.earnXpDescription', 'Earn XP to level up and unlock more rewards'),
    },
    {
      icon: Trophy,
      title: t('referrals.badges', 'Badges'),
      description: t(
        'referrals.badgesDescription',
        `Earn ${REFERRAL_REWARDS.BADGES.ROOKIE.name} (${REFERRAL_REWARDS.BADGES.ROOKIE.threshold}) and ${REFERRAL_REWARDS.BADGES.CHAMPION.name} (${REFERRAL_REWARDS.BADGES.CHAMPION.threshold}) badges`
      ),
    },
  ];

  return (
    <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl p-6 text-white">
      <div className="flex items-center gap-4 mb-6">
        <div className="p-3 bg-white/20 rounded-xl">
          <Gift className="w-8 h-8" />
        </div>
        <div>
          <h2 className="text-xl font-bold">{t('referrals.referralRewards', 'Referral Rewards')}</h2>
          <p className="text-white/80">
            {t('referrals.earnRewardsDescription', 'Earn rewards when your friends join and qualify')}
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {rewards.map((reward, index) => (
          <RewardItem key={index} {...reward} />
        ))}
      </div>

      <div className="mt-4 text-sm text-white/70 text-center">
        <strong>{t('referrals.monthlyCap', 'Monthly cap:')}</strong>{' '}
        {t(
          'referrals.monthlyCapDescription',
          `${REFERRAL_REWARDS.MONTHLY_CAP} Pro Days rewards per month (XP is unlimited)`
        )}
      </div>
    </div>
  );
}
